/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Collapse } from 'react-bootstrap'
import moment from 'moment'
import DatePicker from '@mdsol/lego/lib/DatePicker'
import '@mdsol/lego/lib/DatePicker.css'
import { useServiceContext } from '~/root'
import { TaskFilterProps, TaskViewType } from './types'
import Loader from '~/components/shared/Loader'
import { Select } from '~/components/Select'
import FsiPickerModal from '~/components/ui/FsiPickerModal'
import { InfiniteScrollSelect } from '~/components/InfiniteScrollSelect'
import { useRcmTableData } from '~/hooks/useRcmTableData'
import { getMomentDateFormat, getDatePickerParseFormats } from '~/utils/dateFormatting'

const EOS_TASK_TYPES = ['ACKNOWLEDGE_EOS', 'DOWNLOAD_EOS']

interface FilterFormValues {
  taskTypesCode: string[]
  userId: number[]
  workflowInitiatorId: number[]
  workflowId: any[]
  startFrom: moment.Moment | string | null
  startTo: moment.Moment | string | null
  study: string | null
  site: string | null
  isEosSelected: boolean
  folderId: { id: string; name: string } | null
  data: {
    product: any
    study: any[]
    environment: any
    country: any
    site: any
  }
}

const TasksFilter: FC<TaskFilterProps & { onFilterChange: (filter: any) => void; initialValues?: any }> = ({
  viewType,
  onFilterChange,
  initialValues = {}
}) => {
  const { t } = useTranslation()
  const { api, service } = useServiceContext()
  const tableData = useRcmTableData()
  const currentUser = service.auth.getUser()
  const isEosEnabled = service.init?.getFeaturesInfo?.()?.eosFeature?.show || false
  const isEtmfEnabled = service.init?.getFeaturesInfo?.()?.etmfFeature?.show || false
  const todayDate = moment()

  // Persistence key - matches lego: tableData.persistenceKey + '_filter'
  const persistenceKey = `${tableData.persistenceKey}_filter`

  // Default filter - matches lego implementation
  const getDefaultFilter = (): FilterFormValues => ({
    taskTypesCode: [],
    userId: [],
    workflowInitiatorId: [],
    workflowId: [],
    startFrom: null,
    startTo: null,
    study: null,
    site: null,
    isEosSelected: false,
    folderId: null,
    data: {
      product: null,
      study: [],
      environment: null,
      country: null,
      site: null
    }
  })

  const [taskTypes, setTaskTypes] = useState<Array<{ value: string; label: string }>>([])
  const [users, setUsers] = useState<Array<{ value: number; label: string }>>([])
  const [studies, setStudies] = useState<Array<{ value: string; label: string }>>([])
  const [sites, setSites] = useState<Array<{ value: string; label: string }>>([])
  const [etmfProducts, setEtmfProducts] = useState<Array<{ value: string; label: string }>>([])
  const [etmfStudies, setEtmfStudies] = useState<Array<{ value: string; label: string }>>([])
  const [etmfEnvironments, setEtmfEnvironments] = useState<Array<{ value: string; label: string }>>([])
  const [etmfCountries, setEtmfCountries] = useState<Array<{ value: string; label: string }>>([])
  const [etmfSites, setEtmfSites] = useState<Array<{ value: string; label: string }>>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isFsiPickerOpen, setIsFsiPickerOpen] = useState(false)

  // Filter state
  const [formValues, setFormValues] = useState<FilterFormValues>({
    taskTypesCode: initialValues.taskTypesCode || [],
    userId: initialValues.userId || [],
    workflowInitiatorId: initialValues.workflowInitiatorId || [],
    workflowId: initialValues.workflowId || [],
    startFrom: initialValues.startFrom || null,
    startTo: initialValues.startTo || null,
    study: initialValues.study || null,
    site: initialValues.site || null,
    isEosSelected: initialValues.isEosSelected || false,
    folderId: initialValues.folderId || null,
    data: initialValues.data || {
      product: null,
      study: [],
      environment: null,
      country: null,
      site: null
    }
  })

  useEffect(() => {
    loadInitialData()
  }, [])

  // Load ETMF products when ETMF is enabled - separate useEffect to ensure products load
  useEffect(() => {
    if (isEtmfEnabled) {
      loadEtmfProducts()
    }
  }, [isEtmfEnabled])

  const loadEtmfProducts = async () => {
    try {
      const productsData = await api.etmf.getProducts()
      setEtmfProducts(productsData)
    } catch (error) {
      console.error('Failed to load ETMF products:', error)
      setEtmfProducts([])
    }
  }

  const loadInitialData = async () => {
    setIsLoading(true)
    try {
      // Load users and task types
      const usersData = await api.users.getAssembledUsersGroups()
      // Sort users alphabetically by name - matches lego: Utils.transformToSelectOptions(data.users, 'id', 'name', 'name')
      const userOptions = usersData.users
        .map((u: any) => ({
          value: u.id,
          label: u.name
        }))
        .sort((a: { value: number; label: string }, b: { value: number; label: string }) =>
          a.label.toLowerCase().localeCompare(b.label.toLowerCase())
        )
      setUsers(userOptions)

      const taskTypesData = await api.reports.getTaskTypesMap()
      const taskTypeOptions = Object.entries(taskTypesData)
        .map(([key, val]) => ({
          value: key,
          label: val as string
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
      setTaskTypes(taskTypeOptions)

      // Load persisted filter - matches lego implementation
      // In lego: service.persist.get(persistanceKey)
      // AngularJS persist.get() was route-scoped, so we use getRouteScoped
      const persistedFilter = service.persist.getRouteScoped<FilterFormValues>(persistenceKey)
      const newFilter = persistedFilter || getDefaultFilter()

      // Convert date strings back to moment objects if they exist
      // When persisted to localStorage, moment objects become strings
      if (newFilter.startFrom && typeof newFilter.startFrom === 'string') {
        newFilter.startFrom = moment(newFilter.startFrom)
      }
      if (newFilter.startTo && typeof newFilter.startTo === 'string') {
        newFilter.startTo = moment(newFilter.startTo)
      }

      // Apply view type-specific logic - matches lego switch statement
      switch (viewType) {
        case TaskViewType.COMPLETED:
        case TaskViewType.ALL:
        case TaskViewType.ALL_OPENED:
          // Reset userId filter to empty for these views
          newFilter.userId = []
          break
        case TaskViewType.ME:
          // Set to current user for "My Tasks" view
          if (currentUser) {
            newFilter.userId = [currentUser.id]
          }
          break
      }

      // Set ETMF data structure if missing
      if (!newFilter.data) {
        newFilter.data = {
          product: null,
          study: [],
          environment: null,
          country: null,
          site: null
        }
      }

      setFormValues(newFilter)

      // If EOS is enabled and selected, load EOS studies/sites
      if (isEosEnabled && newFilter.isEosSelected) {
        loadEosStudies()
        if (newFilter.study) {
          loadEosSites(newFilter.study)
        }
      }

      // If ETMF is enabled and we have persisted product data, cascade load the dependent dropdowns
      // Note: Products are loaded by the separate useEffect, so we wait for them
      if (isEtmfEnabled && newFilter.data?.product && etmfProducts.length > 0) {
        const productObj = etmfProducts.find((p: any) => p.value === newFilter.data.product)
        if (productObj) {
          // Cascade load studies -> environments -> countries -> sites
          const studiesData = await api.etmf.getStudies(productObj.value)
          setEtmfStudies(studiesData)

          if (newFilter.data.study && newFilter.data.study.length > 0) {
            const studyIds = newFilter.data.study.map((s: any) => s.value)
            const envsData = await api.etmf.getEnvironments(studyIds)
            setEtmfEnvironments(envsData)

            if (newFilter.data.environment) {
              const countriesData = await api.etmf.getCountries(newFilter.data.environment.value)
              setEtmfCountries(countriesData)

              if (newFilter.data.country) {
                const sitesData = await api.etmf.getSites(newFilter.data.country.value)
                setEtmfSites(sitesData)
              }
            }
          }
        }
      }

      // Apply the loaded/default filter to table context
      // This is crucial - matches lego where persisted filter is applied immediately
      tableData.applyFilter(newFilter)

      // Also notify parent component
      onFilterChange(newFilter)
    } catch (error) {
      console.error('Failed to load filter data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // ETMF Cascade: Product Change -> Load Studies
  // Matches lego: onProductChange(product, level, values, setFieldValue)
  const handleProductChange = async (selectedProduct: any, setFieldValue?: any) => {
    if (!selectedProduct) {
      // Clear all cascading fields
      setEtmfStudies([])
      setEtmfEnvironments([])
      setEtmfCountries([])
      setEtmfSites([])
      return
    }

    try {
      const studiesData = await api.etmf.getStudies(selectedProduct.value)
      setEtmfStudies(studiesData)

      // Auto-select if only one study
      if (studiesData.length === 1) {
        const autoSelectedStudy = [studiesData[0]]
        if (setFieldValue) {
          setFieldValue(
            'data.study',
            autoSelectedStudy.map((s) => s.value)
          )
        }
        await handleStudyChange(autoSelectedStudy, setFieldValue)
      } else if (formValues.data.study && formValues.data.study.length > 0) {
        // Preserve previously selected studies if they still exist
        const existingStudies = studiesData.filter((s) => formValues.data.study.includes(s.value))
        if (existingStudies.length > 0) {
          await handleStudyChange(existingStudies, setFieldValue)
        }
      }
    } catch (error) {
      console.error('Failed to load ETMF studies:', error)
      setEtmfStudies([])
    }
  }

  // ETMF Cascade: Study Change -> Load Environments
  // Matches lego: onStudyChange(selectedStudy, level, values, setFieldValue, studyOpts)
  const handleStudyChange = async (selectedStudies: any[], setFieldValue?: any) => {
    if (!selectedStudies || selectedStudies.length === 0) {
      setEtmfEnvironments([])
      setEtmfCountries([])
      setEtmfSites([])
      return
    }

    // If multiple studies selected, disable downstream selects
    if (selectedStudies.length > 1) {
      setEtmfEnvironments([])
      setEtmfCountries([])
      setEtmfSites([])
      return
    }

    try {
      const environmentsData = await api.etmf.getEnvironments(selectedStudies[0].value)
      setEtmfEnvironments(environmentsData)

      // Auto-select if only one environment
      if (environmentsData.length === 1) {
        const autoSelectedEnv = environmentsData[0]
        if (setFieldValue) {
          setFieldValue('data.environment', autoSelectedEnv.value)
        }
        await handleEnvironmentChange(autoSelectedEnv, setFieldValue)
      } else if (formValues.data.environment) {
        // Preserve previously selected environment if it still exists
        const existingEnv = environmentsData.find((e) => e.value === formValues.data.environment)
        if (existingEnv) {
          await handleEnvironmentChange(existingEnv, setFieldValue)
        }
      }
    } catch (error) {
      console.error('Failed to load ETMF environments:', error)
      setEtmfEnvironments([])
    }
  }

  // ETMF Cascade: Environment Change -> Load Countries
  // Matches lego: onEnvironmentChange(selectedEnv, level, values, setFieldValue, newEnvOpts)
  const handleEnvironmentChange = async (selectedEnv: any, setFieldValue?: any) => {
    if (!selectedEnv || !selectedEnv.value) {
      setEtmfCountries([])
      setEtmfSites([])
      return
    }

    try {
      const countriesData = await api.etmf.getCountries(selectedEnv.value)
      setEtmfCountries(countriesData)

      // Auto-select if only one country
      if (countriesData.length === 1) {
        const autoSelectedCountry = countriesData[0]
        if (setFieldValue) {
          setFieldValue('data.country', autoSelectedCountry.value)
        }
        await handleCountryChange(autoSelectedCountry, setFieldValue)
      } else if (formValues.data.country) {
        // Preserve previously selected country if it still exists
        const existingCountry = countriesData.find((c) => c.value === formValues.data.country)
        if (existingCountry) {
          await handleCountryChange(existingCountry, setFieldValue)
        }
      }
    } catch (error) {
      console.error('Failed to load ETMF countries:', error)
      setEtmfCountries([])
    }
  }

  // ETMF Cascade: Country Change -> Load Sites
  // Matches lego: onCountryChange(selectedCountry, level, values, setFieldValue, countryOpts)
  const handleCountryChange = async (selectedCountry: any, setFieldValue?: any) => {
    if (!selectedCountry || !selectedCountry.value) {
      setEtmfSites([])
      return
    }

    try {
      const sitesData = await api.etmf.getSites(selectedCountry.value)
      setEtmfSites(sitesData)

      // Auto-select if only one site
      if (sitesData.length === 1) {
        if (setFieldValue) {
          setFieldValue('data.site', sitesData[0].value)
        }
      } else if (formValues.data.site) {
        // Preserve previously selected site if it still exists
        const existingSite = sitesData.find((s) => s.value === formValues.data.site)
        if (!existingSite && setFieldValue) {
          setFieldValue('data.site', null)
        }
      }
    } catch (error) {
      console.error('Failed to load ETMF sites:', error)
      setEtmfSites([])
    }
  }

  // EOS: Load Studies
  const loadEosStudies = async () => {
    if (!isEosEnabled) return
    try {
      const studiesData = await api.eos.getAssignedStudies()
      const studyOptions = studiesData.map((s: any) => ({
        value: String(s.studyFolderId),
        label: s.studyName
      }))
      setStudies(studyOptions)
    } catch (error) {
      console.error('Failed to load EOS studies:', error)
      setStudies([])
    }
  }

  // EOS: Study Change -> Load Sites
  const loadEosSites = async (studyFolderId: string) => {
    if (!isEosEnabled || !studyFolderId) return
    try {
      const sitesData = await api.eos.getSitesUnderStudy(Number(studyFolderId), [])
      const siteOptions = sitesData.map((s: any) => ({
        value: String(s.siteFolderId),
        label: s.siteName
      }))
      setSites(siteOptions)
    } catch (error) {
      console.error('Failed to load EOS sites:', error)
      setSites([])
    }
  }

  const handleApplyFilter = () => {
    // Build filter payload with ETMF fsiIds logic (matches WorkflowsFilter.tsx pattern)
    const filterPayload: any = { ...formValues }

    // ETMF: Build fsiIds array from cascade hierarchy (Product -> Study -> Environment -> Country -> Site)
    // Matches legacy DataService.js logic: _.pluck(values.data, 'fsiId')
    if (formValues?.data?.product) {
      const fsiIdArray: (string | null)[] = []

      // Note: formValues.data fields are stored differently:
      // - product, environment, country, site: stored as string values directly
      // - study: stored as array of string values
      fsiIdArray.push(formValues.data.product ?? null) // [0] Product (string value)
      fsiIdArray.push(formValues.data.study?.[0] ?? null) // [1] Study (first value in array)
      fsiIdArray.push(formValues.data.environment ?? null) // [2] Environment (string value)
      fsiIdArray.push(formValues.data.country ?? null) // [3] Country (string value)
      fsiIdArray.push(formValues.data.site ?? null) // [4] Site (string value)

      const studyArray = Array.isArray(formValues.data.study) ? formValues.data.study : []

      if (studyArray.length === 0) {
        // No study selected: use product fsiId only
        filterPayload.fsiIds = [fsiIdArray[0]].filter(Boolean)
      } else if (studyArray.length > 1) {
        // Multiple studies selected: use all study fsiIds (not just first one)
        filterPayload.fsiIds = studyArray.filter(Boolean)
      } else {
        // Single study selected: find the last non-null fsiId in cascade (deepest selection)
        let lastFsiId: string[] = []
        for (let i = 0; i < fsiIdArray.length; i++) {
          if (fsiIdArray[i] == null) {
            // Only access i-1 if we're not at the first element
            if (i > 0) {
              lastFsiId = [fsiIdArray[i - 1] as string] // Last non-null before this
            }
            break
          }
          lastFsiId = [fsiIdArray[fsiIdArray.length - 1] as string] // If never null, use last
        }
        filterPayload.fsiIds = lastFsiId.filter(Boolean)
      }
    }

    // If folderId is selected, append it to fsiIds array
    if (formValues?.folderId) {
      if (filterPayload.fsiIds) {
        filterPayload.fsiIds.push(formValues.folderId.id)
      } else {
        filterPayload.fsiIds = [formValues.folderId.id]
      }
    }

    // Issue 2 Fix: Remove empty arrays that shouldn't be sent to API
    // userId - don't send if empty (no one assigned)
    if (filterPayload.userId && Array.isArray(filterPayload.userId) && filterPayload.userId.length === 0) {
      delete filterPayload.userId
    }
    // workflowInitiatorId - don't send if empty
    if (
      filterPayload.workflowInitiatorId &&
      Array.isArray(filterPayload.workflowInitiatorId) &&
      filterPayload.workflowInitiatorId.length === 0
    ) {
      delete filterPayload.workflowInitiatorId
    }
    // workflowId - don't send if empty
    if (filterPayload.workflowId && Array.isArray(filterPayload.workflowId) && filterPayload.workflowId.length === 0) {
      delete filterPayload.workflowId
    }
    // taskTypesCode - don't send if empty
    if (
      filterPayload.taskTypesCode &&
      Array.isArray(filterPayload.taskTypesCode) &&
      filterPayload.taskTypesCode.length === 0
    ) {
      delete filterPayload.taskTypesCode
    }

    // Remove internal fields that shouldn't be sent to API
    // NOTE: 'study' and 'site' are kept in cleanPayload so index.tsx can map them
    // to studyFolderId/siteFolderId for the api/v2/tasks call.
    // Matches legacy DataService.js: _.omit(filter, 'isEosEnabled', 'isEosSelected', 'data', 'folderId')
    const { data, folderId, isEosSelected, ...cleanPayload } = filterPayload

    // Apply the filter including isEosEnabled/isEosSelected for UI state (TasksTable reads these).
    // Legacy behavior: filter context stores full filter; DataService strips UI-only fields before API call.
    tableData.applyFilter({ ...cleanPayload, isEosEnabled, isEosSelected: isEosSelected || false })

    // Persist original formValues (with data/study/site) for UI state restoration
    service.persist.setRouteScoped(persistenceKey, formValues)

    // Notify parent component with cleaned payload (without UI-only fields)
    onFilterChange(cleanPayload)
  }

  // Matches lego: onReset in Formik which sets filter to defaultFilter
  const handleClearFilter = () => {
    const defaultFilter = getDefaultFilter()

    // Reset form values to default
    setFormValues(defaultFilter)

    // Clear all dropdown states
    setSites([])
    setEtmfStudies([])
    setEtmfEnvironments([])
    setEtmfCountries([])
    setEtmfSites([])

    // Persist the cleared filter
    service.persist.setRouteScoped(persistenceKey, defaultFilter)

    onFilterChange(defaultFilter)
  }

  // Check if current filter equals default filter - for disable state
  const isFilterDefault = () => {
    const defaultFilter = getDefaultFilter()
    return JSON.stringify(formValues) === JSON.stringify(defaultFilter)
  }

  const updateField = (field: keyof FilterFormValues, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div>
        <Loader blocking={isLoading}>
          <div className="filter-wrapper">
            <div className="row">
              <div className="col-sm-4">
                <div className="form-group">
                  <label className="control-label">{t('TASKS_TABLE_HEADER_NAME')}</label>
                  <Select
                    id="task-types"
                    name="taskTypesCode"
                    multi={true}
                    value={formValues.taskTypesCode}
                    options={taskTypes}
                    onChange={(selected) => {
                      const selectedValues = Array.isArray(selected) ? selected.map((s) => s.value) : []
                      updateField('taskTypesCode', selectedValues)

                      if (isEosEnabled) {
                        const eosSelected = selectedValues.some((type: string) => EOS_TASK_TYPES.includes(type))
                        updateField('isEosSelected', eosSelected)

                        if (eosSelected && studies.length === 0) {
                          loadEosStudies()
                        }

                        if (!eosSelected) {
                          updateField('study', null)
                          updateField('site', null)
                          setSites([])
                        }
                      }
                    }}
                    placeholder={t('TASK_TYPES_MULTISELECT_PLACEHOLDER')}
                  />
                </div>

                {/* Workflow Number */}
                <div className="form-group">
                  <label className="control-label">{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}</label>
                  <InfiniteScrollSelect
                    asyncLoadFn={async (search, page) => {
                      const response = await api.workflows.getOptionsForAsyncPicker(search, page)
                      return {
                        options: response.options,
                        hasMore: response.hasMore
                      }
                    }}
                    placeholder={t('WF_NUMBERS_MULTISELECT_PLACEHOLDER')}
                    name="workflowId"
                    onChange={(option) => {
                      updateField('workflowId', option ? option : [])
                    }}
                    value={formValues.workflowId}
                    multi={true}
                    searchable
                    showNewOptionAtTop={true}
                    id="ac6897af-64ed-47c2-aec5-8ab163fe917f"
                  />
                </div>

                {(isEosEnabled || isEtmfEnabled) && (
                  <div className="form-group">
                    <label className="control-label">{t('WORKFLOW_INITIATOR_LABEL')}</label>
                    <Select
                      id="workflow-initiator"
                      name="workflowInitiatorId"
                      multi={true}
                      value={formValues.workflowInitiatorId}
                      options={users}
                      onChange={(selected) => {
                        const selectedValues = Array.isArray(selected) ? selected.map((s) => s.value) : []
                        updateField('workflowInitiatorId', selectedValues)
                      }}
                      placeholder={t('WF_INITIATORS_MULTISELECT_PLACEHOLDER')}
                    />
                  </div>
                )}
                {isEtmfEnabled && (
                  <div className="form-group">
                    <label className="control-label">{t('FOLDER') || 'Folder'}</label>
                    <div>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          setIsFsiPickerOpen(true)
                        }}
                        style={{ fontSize: '14px' }}
                      >
                        {formValues.folderId?.name ?? t('-SELECT_FOLDER-')}
                      </a>
                      {isFsiPickerOpen && (
                        <FsiPickerModal
                          options={{
                            selector: 'folder',
                            showRC: true,
                            showSystem: true,
                            selectedId: Number(formValues?.folderId?.id) || null,
                            leafIds: undefined
                          }}
                          title={t('SELECT_FOLDER')}
                          isOpen={isFsiPickerOpen}
                          onClose={() => {
                            setIsFsiPickerOpen(false)
                          }}
                          onSelect={(fsi: any) => {
                            setIsFsiPickerOpen(false)
                            if (fsi && fsi.id) {
                              updateField('folderId', { id: fsi.id, name: fsi.name })
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="col-sm-4">
                <div className="form-group">
                  <label className="control-label">{t('ASSIGNED_TO')}</label>
                  <Select
                    id="assigned-to"
                    name="userId"
                    multi={true}
                    value={formValues.userId}
                    options={users}
                    onChange={(selected) => {
                      const selectedValues = Array.isArray(selected) ? selected.map((s) => s.value) : []
                      updateField('userId', selectedValues)
                    }}
                    placeholder={t('USER(S)...')}
                  />
                </div>

                {/* EOS Study/Site */}
                {isEosEnabled && (
                  <>
                    <div className="form-group">
                      <label className="control-label">{t('EOS_SETUP_STUDY')}</label>
                      <Select
                        id="eos-study"
                        name="study"
                        value={formValues.study}
                        options={studies}
                        onChange={(selected) => {
                          const studyId = selected && !Array.isArray(selected) ? (selected as any).value : null
                          updateField('study', studyId)
                          updateField('site', null)
                          setSites([])
                          if (studyId) {
                            loadEosSites(studyId)
                          }
                        }}
                        isDisabled={!formValues.isEosSelected}
                        placeholder={t('STUDY...') || 'Select Study...'}
                      />
                    </div>

                    <div className="form-group">
                      <label className="control-label">{t('EMTF_MD_SITE')}</label>
                      <Select
                        id="eos-site"
                        name="site"
                        value={formValues.site}
                        options={sites}
                        onChange={(selected) => {
                          const value = selected && !Array.isArray(selected) ? (selected as any).value : null
                          updateField('site', value)
                        }}
                        isDisabled={!formValues.study}
                        placeholder={t('SITE...') || 'Select Site...'}
                      />
                    </div>
                  </>
                )}

                {/* Start Date (non-EOS) */}
                {!isEosEnabled && (
                  <div>
                    <label style={{ fontSize: '14px' }}>{t('REPORTS_FILTER_LABEL_START_DATE')}</label>
                    <div style={{ paddingLeft: '10px', paddingTop: '4px', paddingRight: '71px' }}>
                      <div className="form-group">
                        <label className="control-label">{t('REPORTS_FILTER_LABEL_FROM')}</label>
                        {/* @ts-expect-error - Lego DatePicker lacks TypeScript definitions */}
                        <DatePicker
                          name="startFrom"
                          onChange={(val: moment.Moment | null) => updateField('startFrom', val)}
                          selected={formValues.startFrom}
                          popoverTargetOffset="3px 0"
                          todayButton="Today"
                          dateFormatCalendar="MMMM"
                          dateFormat={getDatePickerParseFormats()}
                          maxDate={formValues.startTo || todayDate}
                          showYearDropdown
                          className="form-control field-control w100p"
                        />
                      </div>
                      <div className="form-group">
                        <label className="control-label">{t('REPORTS_FILTER_LABEL_TO')}</label>
                        {/* @ts-expect-error - Lego DatePicker lacks TypeScript definitions */}
                        <DatePicker
                          name="startTo"
                          onChange={(val: moment.Moment | null) => updateField('startTo', val)}
                          selected={formValues.startTo}
                          popoverTargetOffset="3px 0"
                          todayButton="Today"
                          dateFormatCalendar="MMMM"
                          minDate={formValues.startFrom}
                          maxDate={todayDate}
                          dateFormat={getDatePickerParseFormats()}
                          showYearDropdown
                          className="form-control field-control w100p"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-sm-4">
                {!isEosEnabled && !isEtmfEnabled && (
                  <div className="form-group">
                    <label className="control-label">{t('WORKFLOW_INITIATOR_LABEL')}</label>
                    <Select
                      id="workflow-initiator-regular"
                      name="workflowInitiatorId"
                      multi={true}
                      value={formValues.workflowInitiatorId}
                      options={users}
                      onChange={(selected) => {
                        const selectedValues = Array.isArray(selected) ? selected.map((s) => s.value) : []
                        updateField('workflowInitiatorId', selectedValues)
                      }}
                      placeholder={t('SELECT_USERS') || 'Select users...'}
                    />
                  </div>
                )}

                {/* ETMF Cascade Filters - Matches lego implementation */}
                {isEtmfEnabled && (
                  <>
                    <div className="form-group">
                      <label className="control-label">{t('EMTF_MD_PRODUCT') || 'Product'}</label>
                      <Select
                        id="etmf-product"
                        name="data.product"
                        value={formValues.data.product}
                        options={etmfProducts}
                        onChange={(selected) => {
                          const selectedProduct = selected && !Array.isArray(selected) ? (selected as any) : null
                          updateField('data', {
                            product: selectedProduct ? selectedProduct.value : null,
                            study: [],
                            environment: null,
                            country: null,
                            site: null
                          })
                          handleProductChange(selectedProduct)
                        }}
                        // isDisabled={!etmfProducts || etmfProducts.length === 0}
                        isClearable={false}
                        placeholder={t('SELECT_PRODUCT') || 'Select Product...'}
                      />
                    </div>

                    <div className="form-group">
                      <label className="control-label">{t('EMTF_MD_STUDY') || 'Study'}</label>
                      <Select
                        id="etmf-study"
                        name="data.study"
                        multi={true}
                        value={Array.isArray(formValues.data.study) ? formValues.data.study : []}
                        options={etmfStudies}
                        onChange={(selected) => {
                          const selectedStudies = Array.isArray(selected) ? selected : []
                          const selectedValues = selectedStudies.map((s: any) => s.value)
                          updateField('data', {
                            ...formValues.data,
                            study: selectedValues,
                            environment: null,
                            country: null,
                            site: null
                          })
                          handleStudyChange(selectedStudies)
                        }}
                        isDisabled={!formValues.data.product || !etmfStudies || etmfStudies.length === 0}
                        isClearable={false}
                        placeholder={t('SELECT_STUDY') || 'Select studies...'}
                      />
                    </div>

                    <div className="form-group">
                      <label className="control-label">{t('EMTF_MD_ENVIRONMENT') || 'Environment'}</label>
                      <Select
                        id="etmf-environment"
                        name="data.environment"
                        value={formValues.data.environment}
                        options={etmfEnvironments}
                        onChange={(selected) => {
                          const selectedEnv = selected && !Array.isArray(selected) ? (selected as any) : null
                          updateField('data', {
                            ...formValues.data,
                            environment: selectedEnv ? selectedEnv.value : null,
                            country: null,
                            site: null
                          })
                          handleEnvironmentChange(selectedEnv)
                        }}
                        isDisabled={
                          !formValues.data.study ||
                          formValues.data.study.length === 0 ||
                          formValues.data.study.length > 1 ||
                          !etmfEnvironments ||
                          etmfEnvironments.length === 0
                        }
                        isClearable={false}
                        placeholder={t('SELECT_ENVIRONMENT') || 'Select Environment...'}
                      />
                    </div>

                    <div className="form-group">
                      <label className="control-label">{t('EMTF_MD_COUNTRY') || 'Country'}</label>
                      <Select
                        id="etmf-country"
                        name="data.country"
                        value={formValues.data.country}
                        options={etmfCountries}
                        onChange={(selected) => {
                          const selectedCountry = selected && !Array.isArray(selected) ? (selected as any) : null
                          updateField('data', {
                            ...formValues.data,
                            country: selectedCountry ? selectedCountry.value : null,
                            site: null
                          })
                          handleCountryChange(selectedCountry)
                        }}
                        isDisabled={!formValues.data.environment || !etmfCountries || etmfCountries.length === 0}
                        isClearable={false}
                        placeholder={t('ETMF_LABEL_SELECT_COUNTRY') || 'Select Country...'}
                      />
                    </div>

                    <div className="form-group">
                      <label className="control-label">{t('REPORTS_FILTER_LABEL_SITE') || 'Site'}</label>
                      <Select
                        id="etmf-site"
                        name="data.site"
                        value={formValues.data.site}
                        options={etmfSites}
                        onChange={(selected) => {
                          const selectedSite = selected && !Array.isArray(selected) ? (selected as any) : null
                          updateField('data', {
                            ...formValues.data,
                            site: selectedSite ? selectedSite.value : null
                          })
                        }}
                        isDisabled={!formValues.data.country || !etmfSites || etmfSites.length === 0}
                        isClearable={false}
                        placeholder={t('SELECT_SITE') || 'Select Site...'}
                      />
                    </div>
                  </>
                )}

                {isEosEnabled && (
                  <div className="form-group">
                    <label className="control-label">{t('REPORTS_FILTER_LABEL_START_DATE')}</label>
                    <div style={{ paddingLeft: '10px', paddingTop: '4px', paddingRight: '71px' }}>
                      <div className="form-group">
                        <label className="control-label">{t('REPORTS_FILTER_LABEL_FROM')}</label>
                        {/* @ts-expect-error - Lego DatePicker lacks TypeScript definitions */}
                        <DatePicker
                          name="startFrom"
                          onChange={(val: moment.Moment | null) => updateField('startFrom', val)}
                          selected={formValues.startFrom}
                          popoverTargetOffset="3px 0"
                          todayButton="Today"
                          dateFormatCalendar="MMMM"
                          dateFormat={getDatePickerParseFormats()}
                          maxDate={formValues.startTo || todayDate}
                          showYearDropdown
                          className="form-control field-control w100p"
                        />
                      </div>
                      <div className="form-group">
                        <label className="control-label">{t('REPORTS_FILTER_LABEL_TO')}</label>
                        {/* @ts-expect-error - Lego DatePicker lacks TypeScript definitions */}
                        <DatePicker
                          name="startTo"
                          onChange={(val: moment.Moment | null) => updateField('startTo', val)}
                          selected={formValues.startTo}
                          popoverTargetOffset="3px 0"
                          todayButton="Today"
                          dateFormatCalendar="MMMM"
                          minDate={formValues.startFrom}
                          maxDate={todayDate}
                          dateFormat={getDatePickerParseFormats()}
                          showYearDropdown
                          className="form-control field-control w100p"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="clearfix">
              <div className="pull-left">
                <button
                  className="btn btn-primary"
                  onClick={handleApplyFilter}
                  disabled={tableData.isLoading}
                  data-action="filter"
                >
                  {t('BTN_FILTER')}
                </button>
                <button
                  className="btn btn-chita btn-link at-reset"
                  onClick={handleClearFilter}
                  disabled={isFilterDefault()}
                  data-action="reset"
                >
                  {t('BTN_CLEAR_FILTER')}
                </button>
              </div>
            </div>
          </div>
        </Loader>
      </div>
    </Collapse>
  )
}

export default TasksFilter
